import { FC, useRef, useState } from 'react'
import styles from './Cart.module.scss'
import cx from 'classnames'
import { Img } from 'components/basic/Img'
import { IconMap } from 'components/_const_assets/icons'
import { Button } from 'components/Buttons/Button'
import { Button as NewButton } from 'components/WebEv/Buttons'
import { CartLineItem } from 'cart'
import { LineItem } from 'components/Cart/LineItem'
import { PriceManager } from 'prices'
import { PriceBlock } from 'components/PriceBlock'
import { AffirmLocations } from 'constants/events'
import { Currency } from 'prices/types'
import { useOnClickOutside } from 'components/_hooks/useOnClickOutside'
import Loader from 'components/Loader'
import FocusTrapWrapper from 'components/FocusTrapWrapper'
import { BundleProducts, MembershipProducts, PillowProducts, PodCoverProducts } from 'products/allProducts'
import { isMembershipProductId, OFFERED_MEMBERSHIPS } from 'products/utils'
import { Product, ProductCore, VariantKey } from 'products/types'
import { A } from 'components/basic/A'

export interface CartProps {
	isLoading: boolean
	isUpdating: boolean

	currency: Currency
	open: boolean

	checkoutButtonText: string

	cartTotal: number
	cartCompareTotal: number

	trialLength: number

	items: CartLineItem[]

	legalese: string

	onCloseCart: () => void
	onCheckout: () => void
	openFinancing: () => void
	onRemoveItem: (item: CartLineItem) => void
	updateItem: (item: CartLineItem, action: string) => void

	showSubtotals: boolean

	cartDescriptions: string[][]

	saleEndDate: Date | string

	hideWarranty?: boolean
	currentMembership: number
	swapPlan: (p: any) => void

	upsellProducts: Product[]
}

export const Cart: FC<CartProps> = (props) => {
	const [modalVisible, setModalVisible] = useState(false)
	const ref = useRef<HTMLDivElement>(null)

	const handleClickOutside = () => {
		if (props.open) {
			props.onCloseCart()
		}
	}

	useOnClickOutside(ref, handleClickOutside, modalVisible)

	let content = <Loader />
	const empty = props.items.length === 0

	if (!props.isLoading) {
		content = (
			<>
				<div className={styles.header}>
					<h2
						className={styles.header_title}
						id={'cart-heading'}
					>
						Your cart {empty && 'is empty'}
					</h2>

					<Button
						id={'cart-close-link'}
						onClick={props.onCloseCart}
					>
						<Img
							alt={'close cart'}
							src={IconMap.x}
						/>
					</Button>
				</div>

				{props.items.length ? (
					<CartContentItems
						{...props}
						setModalVisibility={(b) => setModalVisible(b)}
					/>
				) : (
					<CartContentNoItems {...props} />
				)}
			</>
		)
	}

	return (
		<div
			className={cx(styles.container, { [styles.container_opened]: props.open })}
			role={'dialog'}
			aria-labelledby={'cart-heading'}
		>
			{props.isLoading ? (
				<div
					className={cx(styles.cart_container, { [styles.cart_container_closed]: !props.open })}
					ref={ref}
				>
					{content}
				</div>
			) : (
				<FocusTrapWrapper active={props.open}>
					<div
						className={cx(styles.cart_container, { [styles.cart_container_closed]: !props.open })}
						ref={ref}
					>
						{content}
					</div>
				</FocusTrapWrapper>
			)}

			<div className={cx(styles.shade, { [styles.shade_closed]: !props.open })} />
		</div>
	)
}

interface CartContentProps extends CartProps {
	setModalVisibility: (b: boolean) => void
}

const CartContentItems: FC<CartContentProps> = (props) => {
	const membership = OFFERED_MEMBERSHIPS()[props.currentMembership] as ProductCore | null

	return (
		<div className={`${props.isUpdating ? styles.cart_loading : ''} ${styles.cart_content_padding}`}>
			{props.isUpdating ? <Loader className={styles.loader} /> : <></>}
			{props.items.map((item, i) => {
				const isMembership = isMembershipProductId(item.productId)

				let v = item.variantTitle
				if (item.variantTitle === 'Default Title' || item.variantTitle === 'Pod Mattress' || item.variantTitle === 'Pod Cover') {
					v = ''
				}

				return (
					<LineItem
						key={i}
						id={item.id}
						name={item.productTitle}
						quantity={item.quantity}
						variantId={item.variantId}
						price={item.price / 100}
						hidePrice={item.handle === '8-pro-delayed'}
						comparePrice={(item.comparePrice || 0) / 100}
						variant={v}
						currency={props.currency}
						showVAT={PriceManager.usesVAT(props.currency)}
						onRemove={() => props.onRemoveItem(item)}
						increaseQuantity={() => props.updateItem({ ...item, quantity: item.quantity + 1 }, 'Increase')}
						decreaseQuantity={() => props.updateItem({ ...item, quantity: item.quantity - 1 }, 'Decrease')}
						isMembership={isMembership}
						description={props.cartDescriptions[i]}
						setModalVisibility={props.setModalVisibility}
						currentMembership={props.currentMembership}
						swapPlan={props.swapPlan}
					/>
				)
			})}

			{props.showSubtotals ? (
				<div className={styles.subtotal}>
					<div className={styles.subtotal_line}>
						<strong>Shipping</strong>
						<p>Free</p>
					</div>
					<div className={styles.subtotal_line}>
						<strong>Tax</strong>
						<p>Calculated at check out</p>
					</div>
					<div className={styles.subtotal_line}>
						<strong>Total</strong>
						<p>{PriceManager.formatPriceToCurrencyNoDecimal(props.cartTotal / 100, props.currency)}</p>
					</div>
				</div>
			) : (
				<div className={styles.spacer} />
			)}

			{PriceManager.currencyHasAffirm(props.currency) ? (
				<PriceBlock
					affirmLocation={AffirmLocations.CART}
					amount={props.cartTotal / 100}
					defaultSavingsText={''}
					hideAffirm={false}
					oldAmount={props.cartCompareTotal / 100}
					currency={props.currency}
					region={PriceManager.getCurrencyRegion(props.currency)}
					monthlyPrice={PriceManager.formatPriceToCurrencyNoDecimal(PriceManager.getFinancingAmount(props.cartTotal / 100, PriceManager.getCurrencyRegion(props.currency)), props.currency)}
				/>
			) : null}

			<NewButton.Primary
				id={'cart-checkout-button'}
				size={'large'}
				className={styles.checkout_cta}
				onClick={props.onCheckout}
			>
				{props.checkoutButtonText}
			</NewButton.Primary>

			<div className={cx(styles.icons)}>
				<p>Every purchase comes with</p>
				{membership ? (
					<div className={styles.icon}>
						<Img
							alt=""
							src={IconMap.warranty}
						/>
						<span>{membership.id === MembershipProducts.AutopilotEnhanced.id ? '5' : '2'}-year warranty</span>
					</div>
				) : (
					<></>
				)}
				{props.trialLength ? (
					<div className={styles.icon}>
						<Img
							alt=""
							src={IconMap.moon}
						/>
						{props.trialLength} night risk-free trial
					</div>
				) : null}
				<div className={styles.icon}>
					<Img
						alt=""
						src={IconMap.eight_box}
					/>
					Free returns
				</div>
				<div className={styles.icon}>
					<Img
						alt=""
						src={IconMap.delivery}
					/>
					Free shipping
				</div>
			</div>
			<div>
				<p className={styles.legalese}>
					{props.legalese}
					{props.currency === 'USD' || props.currency === 'CAD' ? (
						<>
							&nbsp;
							<a
								href="#"
								onClick={props.openFinancing}
							>
								Pre-qualify now
							</a>
						</>
					) : null}
				</p>
			</div>
		</div>
	)
}

type NoItemsProps = CartProps

const CartContentNoItems: FC<NoItemsProps> = (props) => {
	const primaryItem = props.upsellProducts[0]
	const primaryItemMetadata = upsellMetadata.get(primaryItem.id)
	const defaultVariant = PriceManager.getRegionDefaultVariant(PriceManager.getCurrencyRegion(props.currency))

	return (
		<div className={styles.no_items_container}>
			<div className={styles.no_items_text}>
				<p>Start your journey to sleep fitness today</p>
				<NewButton.Primary
					size={'large'}
					href={'/pod-cover'}
					id={'no-items-cta'}
					className={styles.get_the_pod}
					onClick={props.onCloseCart}
				>
					Get the Pod
				</NewButton.Primary>
			</div>
		</div>
	)
}

export interface CartShopCardProps {
	image: string
	title: string
	description: string

	href?: string

	specificDiscount?: string
	specificVariant?: VariantKey
}

interface CartShopCardPropsWithPricing extends CartShopCardProps {
	strikePrice: number
	price: number

	currency: Currency
}

const CartShopCard: FC<CartShopCardPropsWithPricing> = (props) => {
	const areThereSavings = props.strikePrice - props.price > 0
	return (
		<A
			className={styles.cart_shop_card}
			href={props.href}
			id={'cart-shop-card-' + props.title.toLowerCase().replace(/ /g, '-')}
		>
			<Img
				src={props.image}
				alt={props.title}
				className={styles.cart_shop_product_image}
			/>

			<strong>{props.title}</strong>
			<p>{props.description}</p>

			<p className={styles.cart_shop_card_price}>
				{areThereSavings && <s>{PriceManager.formatPriceToCurrencyNoDecimal(props.strikePrice, props.currency)}</s>}
				<span>{PriceManager.formatPriceToCurrencyNoDecimal(props.price, props.currency)}</span>
				{/*{props.specificDiscount ? (*/}
				{/*	<span className={styles.cart_shop_card_discount}>{props.specificDiscount}</span>*/}
				{/*) : areThereSavings ? (*/}
				{/*	<span className={styles.cart_shop_card_discount}>{PriceManager.formatPriceToCurrencyNoDecimal(props.price - props.strikePrice, props.currency)}</span>*/}
				{/*) : null}*/}
			</p>
		</A>
	)
}

const upsellMetadata: Map<number | string, CartShopCardProps> = new Map()

upsellMetadata.set(BundleProducts.SleepEssentialBundle.id, {
	title: 'Sleep Essentials',
	description: "Night's necessities",
	image: 'https://eightsleep.imgix.net/Property_1_Sleep_essentials.png?v=1689363940',
	href: '/product/the-sleep-essentials-bundle',
	specificVariant: 'queen',
})

upsellMetadata.set(PodCoverProducts.Pod3CoverPerfect.id, {
	title: 'Pod Cover',
	description: 'Ultimate experience',
	image: 'https://eightsleep.imgix.net/cover-square-p4_shadow_1.png?v=1689092071',
	href: '/product/pod-cover',
})

upsellMetadata.set(PillowProducts.CarbonAirPillow.id, {
	title: 'Carbon Air Pillow',
	description: 'Cooling comfort',
	image: 'https://eightsleep.imgix.net/Property_1_Pillow.png?v=1689363940',
	href: '/product/the-carbon-air-pillow',
	specificVariant: 'onepack',
})
